"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import Image from "next/image";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { id: "nav_homepage", label: "Home", href: "/homepage" },
    { id: "nav_products", label: "Products", href: "/products" },
    { id: "nav_about", label: "About", href: "/about" },
    { id: "nav_contact", label: "Contact", href: "/contact" },
  ];

  return (
    <>
      {/* Hover Zone - Invisible area at top of page (desktop only) */}
      <div
        className="hidden lg:block fixed top-0 left-0 right-0 h-20 z-40"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      />

      <nav
        className={`fixed left-0 right-0 z-50 flex justify-center px-4 transition-all duration-500 ease-in-out ${
          // Mobile/Tablet: Always visible at top
          // Desktop: Hover behavior
          isVisible ? "lg:top-6" : "lg:-top-32"
        } ${
          isScrolled && isVisible ? "lg:top-4" : ""
        } top-0 lg:top-6`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <div
          className={`border border-border flex bg-white/90 w-full max-w-6xl border p-3 shadow-2xl backdrop-blur-xl gap-x-2 items-center justify-between transition-all duration-300 ${
            isScrolled ? "shadow-xl" : ""
          }`}
        >
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <div className="relative w-12 h-12 group-hover:scale-105 transition-transform">
              <Image
                src="/assets/images/Ethical_Minerals_Logo-1770452874374.png"
                alt="Ethical Minerals Uganda Logo"
                fill
                className="object-contain"
                priority
              />
            </div>
            <div className="hidden md:flex flex-col">
              <span className="text-lg font-heading font-semibold text-foreground tracking-tight leading-none">
                Ethical Minerals
              </span>
              <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-mono">
                Uganda
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                className={`px-6 py-2.5 text-[10px] tracking-widest uppercase transition-all duration-300 font-mono font-medium ${
                  pathname === link?.href
                    ? "text-primary bg-primary/5" :"text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <Link
            href="/products"
            className="hidden lg:flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 text-[10px] font-bold tracking-widest uppercase transition-all hover:bg-primary/90 group"
          >
            <span className="font-mono">Explore Catalog</span>
            <Icon
              name="ArrowRightIcon"
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <Icon name="XMarkIcon" size={24} />
            ) : (
              <Icon name="Bars3Icon" size={24} />
            )}
          </button>
        </div>
      </nav>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-background/98 z-40 lg:hidden flex flex-col items-center justify-center gap-8 backdrop-blur-sm">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-primary transition-colors"
            aria-label="Close menu"
          >
            <Icon name="XMarkIcon" size={28} />
          </button>

          {navLinks?.map((link, index) => (
            <Link
              key={link?.id}
              href={link?.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`text-2xl font-heading font-semibold uppercase tracking-wider transition-colors animate-slide-up ${
                pathname === link?.href
                  ? "text-primary" :"text-foreground hover:text-primary"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {link?.label}
            </Link>
          ))}

          <Link
            href="/products"
            onClick={() => setIsMobileMenuOpen(false)}
            className="mt-8 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold tracking-widest uppercase transition-all hover:bg-primary/90 animate-slide-up animate-delay-400"
          >
            Explore Catalog
          </Link>
        </div>
      )}
    </>
  );
}